import { LitElement, html, svg, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin } from '../store.js';
import { withFormats } from '../lib/table.js';
import { OTHER_SLOT, TYPE_SLOTS } from '../lib/palette.js';
import {
  BUMP_MEASURES,
  BUMP_MEASURE_LABELS,
  BUMP_PICKS,
  BUMP_ROWS_DEFAULT,
  bumpColumns,
  bumpExportRows,
  bumpLayout,
  bumpModel,
  bumpSummary,
} from '../lib/bump.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/** What a row of each dimension is called. Matches the heatmap's corner cell. */
const ROW_NOUNS = {
  company: ['Company', 'company'],
  department: ['Department', 'departments'],
  service: ['Service', 'services'],
  office: ['Office', 'offices'],
  individual: ['Person', 'people'],
};

const MEASURE_HINTS = {
  utilization: 'Rank 1 is the most allocated against its own capacity',
  over_hours: 'Rank 1 is the most hours over the line',
  allocated_hours: 'Rank 1 is the most hours allocated, whatever its capacity',
};
const PICK_LABELS = { best_rank: 'Best rank', last_rank: 'Last week', volatility: 'Most movement' };
/** How many lines the chart will draw. Twelve is where a ranking becomes spaghetti. */
const ROW_CHOICES = [6, 12, 20, 40];

/** The plot's own coordinate space. */
const PLOT = Object.freeze({
  width: 760,
  gutter: 150,
  rightGutter: 150,
  rowHeight: 24,
  top: 26,
  dot: 4.5,
  label: 20,
});

const plural = (n, one, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;
const exact = (v) => formatHours(v, { exact: true });

/**
 * The bump chart (Design Review V-14; PRD 10.3; Phase 48): the heatmap read as a ranking over
 * time — which rows are consistently at the top of the risk list, and which are climbing.
 *
 * Every other rendering of this response draws a level. A level answers "how bad is development
 * in week 46"; it does not answer the question the third planning meeting asks about the same
 * roster — *is it the same role every week?* — and the two have different remedies. A role pinned
 * at rank one all quarter is a staffing problem: there are not enough of them and there will not
 * be. A role that was eighth in September and second by November is a pipeline problem: something
 * landed. The dashboard's top-risk list is one week of this chart, and one week cannot tell them
 * apart.
 *
 * Rank is ordinal and distinct, because two lines cannot share a y position; ties are broken by
 * a rule that depends on nothing but the data (more allocated hours, then the label), and the
 * card marks the points where the values were actually equal. A row with no capacity that week
 * has no rank at all — the line breaks rather than dropping to the bottom, because the bottom
 * would be a claim that the row is fine.
 *
 * It draws the response the matrix fetched: no request of its own, and no arithmetic — the
 * ranking, the tie rule and the table live in `lib/bump.js`, which the CSV export imports too.
 */
class BumpChart extends StoreMixin(LitElement) {
  static properties = {
    data: { attribute: false }, // a GET /forecast/heatmap response, or null
    loading: { type: Boolean },
    error: { type: String },
    week: { type: String }, // the page's selected week; this card outlines its column
    _measure: { state: true },
    _rows: { state: true },
    _pick: { state: true },
    _q: { state: true },
    _hover: { state: true },
    _pinned: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.data = null;
    this.loading = false;
    this.error = null;
    this.week = null;
    this._measure = 'utilization';
    this._rows = BUMP_ROWS_DEFAULT;
    this._pick = 'best_rank';
    this._q = '';
    this._hover = null;
    this._pinned = null;
    this._tableOpen = false;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
        min-width: 0;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
        min-width: 0;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 8px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--surface);
        background: var(--ink-strong);
      }
      .field {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .field select,
      .field input {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        padding: 3px 9px;
      }
      .hint {
        font-size: 12px;
        color: var(--ink-muted);
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--brand);
        cursor: pointer;
        padding: 0;
        text-decoration: underline;
      }
      svg.plot {
        display: block;
        width: 100%;
        height: auto;
        overflow: visible;
      }
      path.line {
        fill: none;
        stroke-width: 2.25;
        stroke-linecap: round;
        stroke-linejoin: round;
        cursor: pointer;
      }
      circle.dot {
        cursor: pointer;
      }
      circle.tied {
        stroke: var(--surface);
        stroke-width: 1.5;
      }
      .series[data-dim='true'] path.line,
      .series[data-dim='true'] circle,
      .series[data-dim='true'] text {
        opacity: 0.14;
      }
      .week-line {
        stroke: var(--border-soft);
        stroke-width: 1;
      }
      .week-line[data-selected='true'] {
        stroke: var(--ink-strong);
        stroke-dasharray: 2 3;
      }
      text {
        font-size: 11.5px;
        fill: var(--ink);
      }
      text.axis {
        font-size: 10.5px;
        fill: var(--ink-muted);
      }
      text.rank {
        font-size: 10.5px;
        fill: var(--ink-muted);
        font-variant-numeric: tabular-nums;
      }
      .legends {
        margin: 10px 0 2px;
      }
      ul.legend {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 14px;
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: 11.5px;
        color: var(--ink-muted);
      }
      ul.legend li {
        display: inline-flex;
        align-items: center;
        gap: 5px;
      }
      .sw {
        display: inline-block;
        width: 11px;
        height: 11px;
        border-radius: 2px;
        border: 1px solid var(--border-soft);
      }
      p.note {
        margin: 8px 0 0;
        font-size: 12px;
        line-height: 1.5;
        color: var(--ink-muted);
        max-width: 92ch;
      }
      .state {
        padding: 22px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      data-table {
        display: block;
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    const key = `${this._measure}|${this._rows}|${this._pick}|${this._q}`;
    if (this._modelFor !== this.data || this._modelKey !== key) {
      this._model_ = bumpModel(this.data, {
        measure: this._measure,
        rows: this._rows,
        pick: this._pick,
        q: this._q,
      });
      this._modelFor = this.data;
      this._modelKey = key;
    }
    return this._model_;
  }

  get _nouns() {
    return ROW_NOUNS[this.data?.dimension] ?? ROW_NOUNS.company;
  }

  get _focus() {
    return this._hover ?? this._pinned;
  }

  /**
   * A hue per line, taken from the palette's slots by position rather than by meaning: the rows
   * here are roles and the slots are the project-type hues, so a colour cannot stand for anything
   * — it only has to keep two neighbouring lines apart. Every line is labelled at both ends, the
   * table carries the ranks, and past the eight slots the hues repeat, which is why the chart
   * draws twelve lines and not forty.
   */
  _slotFor(index) {
    const slots = [...TYPE_SLOTS, OTHER_SLOT];
    return slots[index % slots.length].var;
  }

  _selectWeek(weekStart) {
    this.dispatchEvent(
      new CustomEvent('week-change', {
        detail: { week_start: weekStart },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _pin(key) {
    this._pinned = this._pinned === key ? null : key;
  }

  _fmt(value) {
    if (value === null || value === undefined) return '—';
    return this._measure === 'utilization' ? percent(value) : exact(value);
  }

  _show(event, title, rows, footer) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title,
      rows: rows.filter(Boolean),
      footer: footer || '',
    });
  }

  _hide() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  _showPoint(event, row, point) {
    this._show(
      event,
      `${row.label} — ${weekLabel(point.week_start)}`,
      [
        { label: 'Rank', value: `${point.rank} of ${this._model.weeks[point.index].ranks.length}` },
        { label: BUMP_MEASURE_LABELS[this._measure], value: this._fmt(point.value) },
        { label: 'Allocated', value: exact(point.allocated_hours) },
        { label: 'Capacity', value: exact(point.capacity_hours) },
        point.tied ? { label: 'Tied with', value: 'another row on the same value' } : null,
      ],
      point.tied
        ? 'tied on value — the order between them is the tie rule, not the data'
        : 'click the week to draw it everywhere on this page',
    );
  }

  // -------------------------------------------------------------------------------------------
  // The chart

  _plot(model) {
    const inner = PLOT.width - PLOT.gutter - PLOT.rightGutter;
    const layout = bumpLayout(model, {
      width: inner,
      rowHeight: PLOT.rowHeight,
      top: PLOT.top,
    });
    const focus = this._focus;
    const height = layout.height + 16;
    const depth = model.depth;

    return svg`
      <svg
        class="plot"
        viewBox="0 0 ${PLOT.width} ${height}"
        role="img"
        aria-label=${`${plural(layout.lines.length, this._nouns[0].toLowerCase())} ranked over ${plural(
          model.weeks_count,
          'week',
        )}`}
        @pointerleave=${() => {
          this._hover = null;
          this._hide();
        }}
      >
        <g transform=${`translate(${PLOT.gutter} 0)`}>
          ${layout.columns.map(
            (c) => svg`<g>
              <line
                class="week-line"
                data-week=${c.week_start}
                data-selected=${this.week === c.week_start}
                x1=${c.x}
                x2=${c.x}
                y1=${PLOT.top - 10}
                y2=${layout.height}
              ></line>
              <text
                class="axis"
                x=${c.x}
                y=${PLOT.top - 14}
                text-anchor="middle"
                role="button"
                tabindex="0"
                @click=${() => this._selectWeek(c.week_start)}
              >${weekLabel(c.week_start)}</text>
            </g>`,
          )}
          ${Array.from({ length: depth }, (_, i) => i + 1).map(
            (rank) => svg`<text
              class="rank"
              x="-10"
              y=${layout.y(rank)}
              text-anchor="end"
              dominant-baseline="middle"
            >${rank}</text>`,
          )}
          ${repeat(
            layout.lines,
            (l) => l.key,
            (l, i) => {
              const slot = this._slotFor(i);
              const dim = Boolean(focus) && focus !== l.key;
              return svg`<g class="series" data-key=${l.key} data-dim=${dim}>
                ${l.paths.map(
                  (d) => svg`<path
                    class="line mark"
                    role="button"
                    tabindex="0"
                    d=${d}
                    stroke=${`var(${slot})`}
                    @pointerenter=${() => (this._hover = l.key)}
                    @focus=${() => (this._hover = l.key)}
                    @blur=${() => (this._hover = null)}
                    @click=${() => this._pin(l.key)}
                  >
                    <title>${l.row.label}</title>
                  </path>`,
                )}
                ${l.points.map(
                  (p) => svg`<circle
                    class=${`dot mark${p.tied ? ' tied' : ''}`}
                    data-week=${p.week_start}
                    data-rank=${p.rank}
                    cx=${p.x}
                    cy=${p.y}
                    r=${PLOT.dot}
                    fill=${`var(${slot})`}
                    @pointerenter=${(e) => {
                      this._hover = l.key;
                      this._showPoint(e, l.row, p);
                    }}
                    @pointermove=${(e) => this._showPoint(e, l.row, p)}
                    @click=${() => this._selectWeek(p.week_start)}
                  ></circle>`,
                )}
                ${
                  l.start
                    ? svg`<text
                        class="label"
                        x=${l.start.x - 10}
                        y=${l.start.y}
                        text-anchor="end"
                        dominant-baseline="middle"
                      >${
                        l.row.label.length > PLOT.label
                          ? `${l.row.label.slice(0, PLOT.label - 1)}…`
                          : l.row.label
                      }</text>`
                    : nothing
                }
                ${
                  l.end
                    ? svg`<text
                        class="label"
                        x=${l.end.x + 10}
                        y=${l.end.y}
                        dominant-baseline="middle"
                      >${
                        l.row.label.length > PLOT.label
                          ? `${l.row.label.slice(0, PLOT.label - 1)}…`
                          : l.row.label
                      }</text>`
                    : nothing
                }
              </g>`;
            },
          )}
        </g>
      </svg>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls() {
    const group = (options, current, onPick, label) => {
      const slug = label.toLowerCase();
      return html`
        <span class="segmented">
          <span>${label}</span>
          <span class="group" role="group" aria-label=${label}>
            ${options.map(
              ([key, text]) => html`
                <button
                  type="button"
                  data-control=${slug}
                  data-key=${key}
                  aria-pressed=${current === key ? 'true' : 'false'}
                  @click=${() => onPick(key)}
                >
                  ${text}
                </button>
              `,
            )}
          </span>
        </span>
      `;
    };
    return html`
      <div class="controls">
        ${group(
          BUMP_MEASURES.map((m) => [m, BUMP_MEASURE_LABELS[m]]),
          this._measure,
          (k) => (this._measure = k),
          'Rank by',
        )}
        ${group(
          BUMP_PICKS.map((p) => [p, PICK_LABELS[p]]),
          this._pick,
          (k) => (this._pick = k),
          'Keep',
        )}
        <label class="field">
          Lines
          <select @change=${(e) => (this._rows = Number(e.target.value))}>
            ${ROW_CHOICES.map(
              (n) => html`<option value=${n} ?selected=${this._rows === n}>${n}</option>`,
            )}
          </select>
        </label>
        <label class="field">
          Find
          <input
            type="search"
            placeholder=${this._nouns[1]}
            .value=${this._q}
            @input=${(e) => (this._q = e.target.value)}
          />
        </label>
        <span class="hint">${MEASURE_HINTS[this._measure]}</span>
        ${
          this._pinned
            ? html`<button class="link" type="button" @click=${() => (this._pinned = null)}>
                Clear the isolation
              </button>`
            : nothing
        }
      </div>
    `;
  }

  _kpis(summary) {
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${summary.leader ? summary.leader.label : '—'}</div>
          <div class="l">worst in the last week</div>
        </div>
        <div class="kpi">
          <div class="n">${count(summary.pinned.length)}</div>
          <div class="l">never left the top three</div>
        </div>
        <div class="kpi">
          <div class="n">${summary.climber ? `+${count(summary.climber.move)}` : '—'}</div>
          <div class="l">${summary.climber ? summary.climber.label : 'nothing climbing'}</div>
        </div>
        <div class="kpi">
          <div class="n">${count(summary.lead_changes)}</div>
          <div class="l">changes at rank 1</div>
        </div>
      </div>
    `;
  }

  _legend() {
    return html`
      <div class="legends">
        <ul class="legend">
          <li><span class="sw" style="background: var(--ink-muted)"></span>rank 1 is at the top</li>
          <li>A ringed dot is tied on value with another row.</li>
          <li>A broken line is a week the row could not be ranked in.</li>
        </ul>
      </div>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    return `${longDate(model.from)} to ${longDate(model.to)} · ${plural(
      model.weeks_count,
      'week',
    )} · ranked by ${BUMP_MEASURE_LABELS[model.measure].toLowerCase()}`;
  }

  _notes(model, summary) {
    const notes = [];
    notes.push(
      html`<p class="note">
        Each line is one ${this._nouns[0].toLowerCase()}'s place in the week's risk list, worst at
        the top. The question is not how bad a week is — every other rendering of this matrix
        answers that — but whether it is the <strong>same row every week</strong>: a line pinned at
        the top is a staffing problem, and one climbing from the bottom is a pipeline problem.
      </p>`,
    );

    if (summary.pinned.length)
      notes.push(
        html`<p class="note" data-note="pinned">
          ${summary.pinned.map((p) => p.label).join(', ')}
          ${summary.pinned.length === 1 ? 'never leaves' : 'never leave'} the top three all window.
          That is not a week to manage; it is a roster to change.
        </p>`,
      );

    if (summary.climber || summary.faller)
      notes.push(
        html`<p class="note" data-note="movers">
          ${
            summary.climber
              ? html`<strong>${summary.climber.label}</strong> climbed
                  ${plural(summary.climber.move, 'place')} over the window (rank
                  ${summary.climber.first_rank} to ${summary.climber.last_rank}).`
              : nothing
          }
          ${
            summary.faller
              ? html` <strong>${summary.faller.label}</strong> fell
                  ${plural(Math.abs(summary.faller.move), 'place')} (rank
                  ${summary.faller.first_rank} to ${summary.faller.last_rank}).`
              : nothing
          }
        </p>`,
      );

    if (summary.ties > 0)
      notes.push(
        html`<p class="note" data-note="ties">
          ${plural(summary.ties, 'point')} are tied on value with another row. Two lines cannot
          share a height, so the order between them is the tie rule — more allocated hours first,
          then the name — and never a reading of the data. They are ringed.
        </p>`,
      );

    if (summary.gaps > 0 || summary.unrankable > 0)
      notes.push(
        html`<p class="note" data-note="gaps">
          ${
            summary.gaps > 0
              ? html`${plural(summary.gaps, 'week')} of the lines drawn have no rank: no capacity
                that week, so there is no ratio to rank. The line breaks rather than dropping to the
                bottom, which would say the row was fine.`
              : nothing
          }
          ${
            summary.unrankable > 0
              ? html` ${plural(summary.unrankable, this._nouns[0].toLowerCase())} could not be
                ranked in any week at all and are in the table only.`
              : nothing
          }
        </p>`,
      );

    if (summary.hidden > 0)
      notes.push(
        html`<p class="note" data-note="hidden">
          ${plural(summary.hidden, this._nouns[0].toLowerCase())} are not drawn — twelve lines is
          where a ranking becomes a plate of spaghetti. Every row is ranked against every other one
          whatever is drawn, so hiding a line never promotes another, and the table carries them
          all.
        </p>`,
      );

    return html`${notes}`;
  }

  get _formats() {
    return {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : exact(v)),
        percent: (v) => percent(v),
        count: (v) => (v === null || v === undefined ? '—' : count(v)),
        number: (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${count(v)}`),
      },
    };
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const model = this._model;
    const summary = model ? bumpSummary(model) : null;

    let body;
    if (this.error) body = html`<div class="state error">${this.error}</div>`;
    else if (!this.data && this.loading) body = html`<div class="state">Ranking the weeks…</div>`;
    else if (!model)
      body = html`<div class="state">No rows in this window, so there is nothing to rank.</div>`;
    else if (!model.series.length)
      body = html`
        ${this._controls()}
        <div class="state" data-note="empty">
          Not one row can be ranked in any week: without capacity there is no ratio to order by.
        </div>
      `;
    else
      body = html`
        ${this._controls()} ${this._plot(model)} ${this._legend()} ${this._notes(model, summary)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Rank per week — ${this._subtitle(model)}`}
            export-name="bump-ranks"
            page-size="25"
            dense
            .columns=${withFormats(bumpColumns({ weeks: model.weeks }), this._formats)}
            .rows=${bumpExportRows(model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this.loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Risk rank over time</div>
            <h2>Is it the same ${this._nouns[0].toLowerCase()} every week?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${summary ? this._kpis(summary) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('bump-chart', BumpChart);
